import React, { FunctionComponent, useRef, useState } from "react";
import LoadingIndicator from "../../LoadingIndicator";
import { Button, Form, InputGroup, Nav, OverlayTrigger, Popover, Tab, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt, faPlusCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import EditStudentForm from "./StudentEditForm";
import { getFullNameForStudent } from "../../../../../utils/StudentMapUtils";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import {
  changeSelectedClass,
  changeSelectedStudent,
  openAssignStudentsToEmployeeModal,
  openCreateClassModal,
  openStudentEditForm,
} from "../../../../../store/onboarding/actions";
import { connect } from "react-redux";
import {
  Student,
  TeacherClass,
  TeacherInfo,
} from "../../../../../store/onboarding/types";
import { EventKey } from "react-bootstrap/esm/types";
import useUserRole from "../../../../../utils/hooks/useUserRole";

type StateProps = {
  selectedTeacher?: TeacherInfo;
  teachersClasses: Array<TeacherClass>;
  teachersStudents: Array<Student>;
  isLoading: {
    getStudentsAssignedToTeacher: boolean;
    getClassesAssignedToTeacher: boolean;
  };
};

type DispatchProps = {
  openStudentEditForm: () => any;
  openCreateClassModal: () => any;
  changeSelectedStudent: (selectedStudent?: Student) => any;
  changeSelectedClass: (selectedClass?: TeacherClass) => any;
  openAssignStudentsToEmployeeModal: () => any;
};

type Props = StateProps & DispatchProps;

enum TabKeys {
  FIRST = "classes",
  SECOND = "students",
}

const TeacherTab: FunctionComponent<Props> = ({
  selectedTeacher,
  teachersClasses,
  teachersStudents,
  isLoading,
  openCreateClassModal,
  changeSelectedStudent,
  changeSelectedClass,
  openStudentEditForm,
  openAssignStudentsToEmployeeModal,
}) => {
  const [activeTab, setActiveTab] = useState<string | null>(TabKeys.SECOND);
  const { isCoach } = useUserRole();
  const [studentSearchFilter, setstudentSearchFilter] = useState<string>("");
  const addStudentButtonRef = useRef<HTMLButtonElement>(null);

  if (
    isLoading.getStudentsAssignedToTeacher ||
    isLoading.getClassesAssignedToTeacher
  ) {
    return <LoadingIndicator />;
  }

  const handleSelectedStudentSelect = (student: Student) => () => {
    changeSelectedStudent(student);
    openStudentEditForm();
  };

  const handleEditStudentFormOpen = () => {
    // Immediately blur the button to remove focus state
    if (addStudentButtonRef.current) {
      addStudentButtonRef.current.blur();
    }
    changeSelectedStudent();
    openAssignStudentsToEmployeeModal();
  };

  const handleEditClassModalOpen = (teachersClass: TeacherClass) => () => {
    changeSelectedClass(teachersClass);
    openCreateClassModal();
  };

   const getSorted = (a: Student, b: Student) => {
    const query = studentSearchFilter.toLowerCase();
    const isNumeric = /^\d+$/.test(query);

    const aName = getFullNameForStudent(a).toLowerCase();
    const bName = getFullNameForStudent(b).toLowerCase();

    const aId = a.unique_id?.toString().toLowerCase() || "";
    const bId = b.unique_id?.toString().toLowerCase() || "";

    if (isNumeric) {
      const aStarts = aId.startsWith(query);
      const bStarts = bId.startsWith(query);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      const aIndex = aId.indexOf(query);
      const bIndex = bId.indexOf(query);

      return aIndex - bIndex;
    } else {
      const aNameStarts = aName.startsWith(query);
      const bNameStarts = bName.startsWith(query);

      if (aNameStarts !== bNameStarts) {
        return aNameStarts ? -1 : 1;
      }

      const aNameIndex = aName.indexOf(query);
      const bNameIndex = bName.indexOf(query);

      return aNameIndex - bNameIndex;
    }
  };

  return (
    <Tab.Container activeKey={activeTab as EventKey} onSelect={setActiveTab}>
      <div className="d-flex align-items-center">
        <Nav className="d-inline">
          <Nav.Item className="teacherDetailsTab">
            <Nav.Link eventKey={TabKeys.SECOND} className="default-pointer">
              <>
                {`Students (${teachersStudents.length})`}

                {!isCoach && (teachersStudents.length) > 0 ? <>
                  <OverlayTrigger
                    trigger="hover"
                    overlay={<Popover id="add-student-tooltip" className="p-2">Assign student(s) — changes apply immediately.</Popover>}
                  >
                    <Button 
                      ref={addStudentButtonRef} 
                      onClick={handleEditStudentFormOpen} 
                      className="ml-2"
                      style={{
                        outline: 'none',
                        boxShadow: 'none'
                      }}
                      onMouseDown={(e) => {
                        // Prevent the button from maintaining focus after click
                        e.currentTarget.style.outline = 'none';
                      }}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} className="pointer" />
                    </Button>
                  </OverlayTrigger>
                </> : null}
                <EditStudentForm />
              </>
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>
      <hr />
      <Tab.Content>
        <Tab.Pane eventKey={TabKeys.SECOND}>
          {teachersStudents.length > 0 ? (
            <div className="pl-0">
              <InputGroup className="filterSearchDesktop mb-3" size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text className="search-icon">
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={studentSearchFilter as any}
                  onChange={(e) => setstudentSearchFilter(e.target.value)}
                  className="filterSearchHeight"
                />
              </InputGroup>
              <div
                style={{
                  maxHeight: 342,
                  overflowY: "auto",
                  marginTop: 10,
                  scrollbarWidth: "thin"
                }}
                className="sticky-table-header-wrapper"
              >
                {teachersStudents.length > 0
                  ?
                  <Table className="goalTable table-sm">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Student ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teachersStudents
                        .filter((student) => {
                          const fullName = getFullNameForStudent(student);
                          return (
                            fullName.toLowerCase().includes(studentSearchFilter.toLowerCase()) ||
                            student.unique_id?.toLowerCase().includes(studentSearchFilter.toLowerCase())
                          );
                        })
                        .sort(getSorted)
                        .map((student) => (
                          <tr key={student.unique_id}>
                            <td>{getFullNameForStudent(student)}{" "}</td>
                            <td>{student.unique_id}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  : ''}
              </div>
            </div>
          ) : (
            <div className="classSetupContainerEmpty">
              <div>
                <h4 className="mb-3">{`${selectedTeacher?.user.first_name} doesn't have any associated students yet`}</h4>
                {!isCoach ? <Button
                  onClick={handleEditStudentFormOpen}
                  className="blueBtnSm"
                  bsPrefix="b"
                >
                  Assign Student(s)
                </Button> : ''}
              </div>
            </div>
          )}
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );
};

const mapStateToProps = ({ onboarding }: ApplicationState): StateProps => {
  return {
    selectedTeacher: onboarding.selectedTeacher,
    teachersClasses: onboarding.teachersClasses,
    teachersStudents: onboarding.teachersStudents,
    isLoading: {
      getClassesAssignedToTeacher:
        onboarding.isLoading.getClassesAssignedToTeacher,
      getStudentsAssignedToTeacher:
        onboarding.isLoading.getStudentsAssignedToTeacher,
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return bindActionCreators(
    {
      changeSelectedStudent: changeSelectedStudent,
      changeSelectedClass: changeSelectedClass,
      openStudentEditForm: openStudentEditForm,
      openCreateClassModal: openCreateClassModal,
      openAssignStudentsToEmployeeModal: openAssignStudentsToEmployeeModal,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TeacherTab);
